<script setup lang="ts">
/**
 * SceneAway · 离开小剧场：走廊上走来走去
 * ------------------------------------------------------------
 * 舞台 = 没有窗户的一段地面：小人迈着两条腿来回散步，走到头
 * 挤压转身折返；悬停时步子加快（hot 由外壳传入）。
 * 人物比例与所有场景统一：头 r6.6、0.8px/viewBox 单位（见 scenes/index.ts）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" aria-hidden="true">
    <i class="floor" />
    <span class="walk">
      <span class="waddle">
        <svg viewBox="0 0 20 34">
          <defs>
            <linearGradient id="apShirtAway" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#7A95FF" />
              <stop offset="1" stop-color="#4C6BF0" />
            </linearGradient>
          </defs>
          <!-- 腿（前后交替摆） + 鞋 -->
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="-24 8.6 24.6;24 8.6 24.6;-24 8.6 24.6" dur=".52s" repeatCount="indefinite" />
            <rect x="7.75" y="24" width="1.7" height="6" rx=".85" fill="#3D55C8" />
            <rect x="7.6" y="29.4" width="2.5" height="1.7" rx=".8" fill="#2B2118" />
          </g>
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="24 11.4 24.6;-24 11.4 24.6;24 11.4 24.6" dur=".52s" repeatCount="indefinite" />
            <rect x="10.55" y="24" width="1.7" height="6" rx=".85" fill="#3D55C8" />
            <rect x="10.4" y="29.4" width="2.5" height="1.7" rx=".8" fill="#2B2118" />
          </g>
          <!-- 身体（连帽衫） -->
          <rect x="4.4" y="14.6" width="11.2" height="10.4" rx="3.5" fill="url(#apShirtAway)" />
          <path d="M7.3 15.8q2.7 2.1 5.4 0" stroke="#FFFFFF" stroke-opacity=".5" stroke-width=".7" fill="none" stroke-linecap="round" />
          <!-- 前摆的手臂 -->
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="18 13.8 16.4;-20 13.8 16.4;18 13.8 16.4" dur=".52s" repeatCount="indefinite" />
            <rect x="12.9" y="15.8" width="1.9" height="5.6" rx=".95" fill="#4C6BF0" />
            <circle cx="13.85" cy="21.7" r="1" fill="#FFDCB4" stroke="#E6B183" stroke-width=".5" />
          </g>
          <!-- 头（正面，会眨） -->
          <g>
            <circle cx="10" cy="8.4" r="6.6" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
            <!-- 头发 + 呆毛 -->
            <path d="M3.5 7.9a6.5 6.5 0 0 1 13 0v.8H3.5z" fill="#3A2B21" />
            <path d="M9.5 2.6c1.1-1.6 2.7-1.9 3.5-1.4-1.3.4-2.1 1-2.5 1.8z" fill="#3A2B21" />
            <!-- 眉 -->
            <path d="M6.9 5.9q1.1-.7 2.2-.2M13.1 5.9q-1.1-.7-2.2-.2" stroke="#3A2B21" stroke-width=".7"
                  fill="none" stroke-linecap="round" opacity=".75" />
            <!-- 眼（会眨） -->
            <ellipse cx="7.9" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="3.8s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="12.1" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="3.8s" repeatCount="indefinite" />
            </ellipse>
            <circle cx="8.5" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <circle cx="12.7" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <!-- 腮红 -->
            <ellipse cx="5.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <ellipse cx="14.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <!-- 笑 -->
            <path d="M8.6 11.6a1.5 1.5 0 0 0 2.8 0z" fill="#C05A5A" />
          </g>
        </svg>
      </span>
    </span>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 地面光斑：一段可以散步的地面 */
.floor {
  position: absolute; left: 50%; bottom: 2px;
  width: 40px; height: 6px; margin-left: -20px; border-radius: 50%;
  background: radial-gradient(closest-side, #1a3a6626, transparent);
}

/* 走路：外层负责左右走 + 到头转身翻转，内层负责迈步的摇摆起伏；
   抬高 2px：高个站姿与窗台小人保持同一条头部水平线 */
.walk {
  position: absolute; left: 50%; bottom: 2px;
  width: 16px; margin-left: -13px;
  transform-origin: 50% 100%;
  filter: drop-shadow(0 1px 1px #1a3a6626);
  animation: awWalk 9s ease-in-out infinite;
}
/* 0.8px/viewBox 单位：20×34 → 16×27.2，人物头径与 online 场景一致 */
.walk svg { display: block; width: 16px; height: 27.2px; }
.sc.hot .walk { animation-duration: 6s; }
@keyframes awWalk {
  0%   { transform: translateX(0) scaleX(1); }
  40%  { transform: translateX(13px) scaleX(1); }
  44%  { transform: translateX(13px) scaleX(.35); }
  48%  { transform: translateX(13px) scaleX(-1); }
  88%  { transform: translateX(0) scaleX(-1); }
  92%  { transform: translateX(0) scaleX(.35); }
  96%  { transform: translateX(0) scaleX(1); }
  100% { transform: translateX(0) scaleX(1); }
}
.waddle { display: block; animation: awWaddle .5s ease-in-out infinite alternate; }
@keyframes awWaddle {
  from { transform: rotate(-4deg) translateY(0); }
  to   { transform: rotate(4.5deg) translateY(-.7px); }
}
</style>
